import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, Field } from '../components/ui';

/**
 * Velg nytt passord, fra lenken i e-posten.
 *
 * Tokenet står i spørrestrengen og ikke i stien, slik at det aldri havner i en
 * `Referer`-header hvis siden skulle lenke ut. Den logger ikke inn etterpå:
 * brukeren skriver det nye passordet én gang til på innloggingssiden, og da
 * vet de at det virker.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [passord, setPassord] = useState('');
  const [gjenta, setGjenta] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const [jobber, setJobber] = useState(false);

  if (!token) {
    return (
      <AuthLayout title="Lenken mangler" subtitle="Adressen ser ikke komplett ut.">
        <Alert>
          Vi fant ingen lenke i adressen. Kopierte du den fra e-posten, kan den ha blitt delt over
          to linjer.
        </Alert>
        <p className="small">
          <Link to="/glemt-passord">Be om en ny lenke</Link>
        </p>
      </AuthLayout>
    );
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // Sjekkes her og ikke bare på serveren: å oppdage en skrivefeil etter at
    // engangslenken er brukt opp, ville vært et dårlig sted å oppdage den.
    if (passord !== gjenta) {
      setFeil('De to passordene er ikke like.');
      return;
    }
    setJobber(true);
    setFeil(null);
    try {
      await api.post('/auth/reset-password', { token, password: passord });
      navigate('/logg-inn?passord=endret', { replace: true });
    } catch (caught) {
      setFeil(caught instanceof ApiError ? caught.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setJobber(false);
    }
  };

  return (
    <AuthLayout title="Velg nytt passord" subtitle="Minst ti tegn. Gjerne noe langt og enkelt å huske.">
      <form onSubmit={onSubmit} noValidate>
        <Alert>{feil}</Alert>

        <Field label="Nytt passord">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            autoFocus
            value={passord}
            onChange={(event) => setPassord(event.target.value)}
          />
        </Field>

        <Field label="Gjenta passordet">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={gjenta}
            onChange={(event) => setGjenta(event.target.value)}
          />
        </Field>

        <button className="btn btn-primary btn-block" type="submit" disabled={jobber}>
          {jobber ? 'Lagrer …' : 'Lagre nytt passord'}
        </button>
      </form>

      <p className="tiny muted" style={{ marginTop: '1rem' }}>
        Alle som er logget inn på kontoen fra før, blir logget ut.
      </p>
    </AuthLayout>
  );
}
